import React, { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import GridOnOutlinedIcon from '@mui/icons-material/GridOnOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import AddToPhotosOutlinedIcon from '@mui/icons-material/AddToPhotosOutlined';
import CheckIcon from '@mui/icons-material/Check';
import { downloadArtifactCsv, downloadArtifactDoc } from '../utils/boardChatArtifacts';

function TablePreview({ table }) {
  if (!table?.columns?.length) return null;
  const rows = (table.rows || []).slice(0, 40);
  return (
    <Box className="board-chat-artifact-table-wrap">
      <table className="board-chat-artifact-table">
        <thead>
          <tr>
            {table.columns.map(col => (
              <th key={col}>{col}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {table.columns.map((_, colIndex) => (
                <td key={colIndex}>{row?.[colIndex] ?? ''}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {(table.rows || []).length > rows.length ? (
        <Typography variant="caption" color="text.secondary" className="board-chat-artifact-more">
          +{(table.rows || []).length - rows.length} more rows (download for full table)
        </Typography>
      ) : null}
    </Box>
  );
}

function DocPreview({ doc }) {
  const body = String(doc?.body || '').trim();
  if (!body) return null;
  return (
    <Box className="board-chat-artifact-doc">
      {body.split(/\n/).map((line, index) => (
        <Typography
          key={index}
          variant="body2"
          component="p"
          className="board-chat-artifact-doc-line"
        >
          {line || '\u00a0'}
        </Typography>
      ))}
    </Box>
  );
}

/**
 * Renders synthesize → render_table / render_doc results in chat.
 */
export default function BoardChatArtifact({ artifact, onPlaceOnCanvas }) {
  const [placed, setPlaced] = useState(false);
  if (!artifact?.kind) return null;

  const isTable = artifact.kind === 'table';
  const Icon = isTable ? GridOnOutlinedIcon : DescriptionOutlinedIcon;

  const handleDownload = () => {
    if (isTable) downloadArtifactCsv(artifact);
    else downloadArtifactDoc(artifact);
  };

  const handlePlace = () => {
    if (!onPlaceOnCanvas || placed) return;
    const ok = onPlaceOnCanvas(artifact);
    if (ok !== false) setPlaced(true);
  };

  return (
    <Box className="board-chat-artifact">
      <Box className="board-chat-artifact-header">
        <Icon className="board-chat-artifact-icon" fontSize="small" />
        <Typography variant="caption" className="board-chat-artifact-title">
          {artifact.title || (isTable ? 'Table' : 'Document')}
        </Typography>
      </Box>
      {isTable ? <TablePreview table={artifact.table} /> : <DocPreview doc={artifact.doc} />}
      <Box className="board-chat-artifact-actions">
        <Button
          size="small"
          variant="outlined"
          startIcon={<DownloadOutlinedIcon />}
          onClick={handleDownload}
          className="board-chat-artifact-btn"
        >
          {isTable ? 'Download CSV' : 'Download .md'}
        </Button>
        {onPlaceOnCanvas ? (
          <Button
            size="small"
            variant="contained"
            startIcon={placed ? <CheckIcon /> : <AddToPhotosOutlinedIcon />}
            onClick={handlePlace}
            disabled={placed}
            className="board-chat-artifact-btn"
          >
            {placed ? 'On canvas' : 'Add to canvas'}
          </Button>
        ) : null}
      </Box>
    </Box>
  );
}
